import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, AlertTriangle, UserX, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';

interface EndEmploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSuccess?: () => void;
}

interface TerminationResponse {
  success: boolean;
  message: string;
  timeline_entry_id?: number;
  termination_record_id?: number;
  final_working_day?: string;
  holiday_pay_amount?: number;
  notice_pay_amount?: number;
  p45_required?: boolean;
  error?: string;
  conflicts?: string[];
}

interface TerminationData {
  termination_date: string;
  termination_reason: string;
  notice_period_start_date?: string;
  notice_period_end_date?: string;
  is_payment_in_lieu_of_notice: boolean;
  is_garden_leave: boolean;
  holiday_pay_owed: boolean;
  notes?: string;
}

const TERMINATION_REASONS = [
  { value: 'resignation', label: 'Employee Resignation' },
  { value: 'dismissal', label: 'Dismissal' },
  { value: 'redundancy', label: 'Redundancy' },
  { value: 'mutual_agreement', label: 'Mutual Agreement' },
  { value: 'end_of_contract', label: 'End of Fixed-term Contract' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'other', label: 'Other' },
];

export const EndEmploymentModal: React.FC<EndEmploymentModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<TerminationData>({
    termination_date: '',
    termination_reason: '',
    notice_period_start_date: '',
    notice_period_end_date: '',
    is_payment_in_lieu_of_notice: false,
    is_garden_leave: false,
    holiday_pay_owed: true,
    notes: '',
  });
  const [selectedTerminationDate, setSelectedTerminationDate] = useState<Date>();
  const [selectedNoticePeriodStart, setSelectedNoticePeriodStart] = useState<Date>();
  const [selectedNoticePeriodEnd, setSelectedNoticePeriodEnd] = useState<Date>();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      termination_date: '',
      termination_reason: '',
      notice_period_start_date: '',
      notice_period_end_date: '',
      is_payment_in_lieu_of_notice: false,
      is_garden_leave: false,
      holiday_pay_owed: true,
      notes: '',
    });
    setSelectedTerminationDate(undefined);
    setSelectedNoticePeriodStart(undefined);
    setSelectedNoticePeriodEnd(undefined);
    setErrors([]);
    setShowAdvancedOptions(false);
  };

  const handleTerminationDateSelect = (date: Date | undefined) => {
    setSelectedTerminationDate(date);
    if (date) {
      setFormData(prev => ({
        ...prev,
        termination_date: format(date, 'yyyy-MM-dd'),
      }));
    }
    // Clear errors when user selects date
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleNoticePeriodStartSelect = (date: Date | undefined) => {
    setSelectedNoticePeriodStart(date);
    if (date) {
      setFormData(prev => ({
        ...prev,
        notice_period_start_date: format(date, 'yyyy-MM-dd'),
      }));
    }
  };

  const handleNoticePeriodEndSelect = (date: Date | undefined) => {
    setSelectedNoticePeriodEnd(date);
    if (date) {
      setFormData(prev => ({
        ...prev,
        notice_period_end_date: format(date, 'yyyy-MM-dd'),
      }));
    }
  };

  const handleInputChange = (field: keyof TerminationData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear errors when user makes changes
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.termination_date) {
      newErrors.push('Please select a termination date');
    } else {
      const terminationDate = new Date(formData.termination_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (terminationDate < today) {
        newErrors.push('Termination date cannot be in the past');
      }
    }

    if (!formData.termination_reason) {
      newErrors.push('Please select a termination reason');
    }

    // Validate notice period dates if provided
    if (formData.notice_period_start_date && formData.notice_period_end_date) {
      const startDate = new Date(formData.notice_period_start_date);
      const endDate = new Date(formData.notice_period_end_date);
      
      if (startDate > endDate) {
        newErrors.push('Notice period start date must be before end date');
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await ApiService.request<TerminationResponse>(
        EndpointKeys.CREATE_EMPLOYMENT_TERMINATION,
        {
          method: 'POST',
          urlParams: { employeeId: employeeId.toString() },
          body: formData,
        }
      );

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
        onSuccess?.();
        onClose();
      } else {
        if (response.conflicts && response.conflicts.length > 0) {
          setErrors(response.conflicts);
        } else {
          setErrors([response.error || 'Failed to create employment termination']);
        }
      }
    } catch (error: any) {
      console.error('Employment termination error:', error);
      if (error.response?.data?.conflicts) {
        setErrors(error.response.data.conflicts);
      } else if (error.response?.data?.error) {
        setErrors([error.response.data.error]);
      } else {
        setErrors(['Failed to create employment termination. Please try again.']);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5" />
            End Employment
          </DialogTitle>
          <DialogDescription>
            Terminate employment for this employee. This action will create a termination record and schedule the end of employment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Termination Date */}
          <div className="space-y-2">
            <Label>
              Last Working Day <span className="text-red-500">*</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 ml-1 inline" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>The employee's final day of work</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedTerminationDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedTerminationDate ? format(selectedTerminationDate, 'dd/MM/yyyy') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedTerminationDate}
                  onSelect={handleTerminationDateSelect}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Termination Reason */}
          <div className="space-y-2">
            <Label htmlFor="termination_reason">
              Termination Reason <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.termination_reason}
              onValueChange={(value) => handleInputChange('termination_reason', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select termination reason" />
              </SelectTrigger>
              <SelectContent>
                {TERMINATION_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Holiday Pay */}
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label>Holiday Pay Owed</Label>
              <p className="text-sm text-muted-foreground">
                Include accrued holiday pay in final payment
              </p>
            </div>
            <Switch
              checked={formData.holiday_pay_owed}
              onCheckedChange={(checked) => handleInputChange('holiday_pay_owed', checked)}
            />
          </div>

          {/* Advanced Options Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              checked={showAdvancedOptions}
              onCheckedChange={setShowAdvancedOptions}
            />
            <Label>Show advanced options</Label>
          </div>

          {/* Advanced Options */}
          {showAdvancedOptions && (
            <div className="space-y-4 border-t pt-4">
              {/* Notice Period */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Notice Period Start</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !selectedNoticePeriodStart && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedNoticePeriodStart ? format(selectedNoticePeriodStart, 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedNoticePeriodStart}
                        onSelect={handleNoticePeriodStartSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Notice Period End</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !selectedNoticePeriodEnd && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedNoticePeriodEnd ? format(selectedNoticePeriodEnd, 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedNoticePeriodEnd}
                        onSelect={handleNoticePeriodEndSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Payment in Lieu of Notice */}
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label>Payment in Lieu of Notice (PILON)</Label>
                  <p className="text-sm text-muted-foreground">
                    Pay instead of working notice period
                  </p>
                </div>
                <Switch
                  checked={formData.is_payment_in_lieu_of_notice}
                  onCheckedChange={(checked) => handleInputChange('is_payment_in_lieu_of_notice', checked)}
                />
              </div>

              {/* Garden Leave */}
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label>Garden Leave</Label>
                  <p className="text-sm text-muted-foreground">
                    Employee remains on payroll but doesn't work
                  </p>
                </div>
                <Switch
                  checked={formData.is_garden_leave}
                  onCheckedChange={(checked) => handleInputChange('is_garden_leave', checked)}
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              placeholder="Enter any additional notes about the termination..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant="destructive"
          >
            {isSubmitting ? 'Processing...' : 'End Employment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
